import { filterAllowedSearchParams } from './searchParams';

/**
 * Entry params live in sessionStorage, not in the URL: read once on landing, merged over the earlier
 * state of this tab (URL wins per key), then the address bar is cleaned. Reloads and in-app navigation
 * keep working from the stored copy, and the user never sees `?onboardingVariant=…&utm_…` again. A new tab starts
 * clean, like WebQuiz (one tab = one funnel run).
 */
export const ENTRY_STORAGE_KEY = '[[entry]]';

/** Provider redirects land with these — the payments return handler reads them from the live URL. */
const PAYMENT_RETURN_KEYS = [
  'payment_intent_client_secret',
  'setup_intent_client_secret',
  'redirect_status',
  'payment_success',
  'payment_method',
  'subscription_id',
  'token',
  'ba_token',
];

interface EntryWindow {
  location: { search: string; pathname: string; hash: string };
  history: { state: unknown; replaceState: (state: unknown, title: string, url: string) => void };
}

interface EntryStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const readStored = (storage: EntryStorage): URLSearchParams => {
  try {
    const raw = storage.getItem(ENTRY_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return filterAllowedSearchParams(new URLSearchParams(Array.isArray(parsed) ? (parsed as string[][]) : []));
  } catch {
    return new URLSearchParams();
  }
};

const hasPaymentReturn = (params: URLSearchParams): boolean => PAYMENT_RETURN_KEYS.some((key) => params.has(key));

export const captureEntryParams = (win: EntryWindow, storage: EntryStorage): URLSearchParams => {
  const fromUrl = filterAllowedSearchParams(win.location.search);
  const merged = readStored(storage);
  for (const [key, value] of fromUrl) merged.set(key, value);

  try {
    storage.setItem(ENTRY_STORAGE_KEY, JSON.stringify([...merged.entries()]));
  } catch {
    // Storage unavailable (private mode, quota): params still work for this page load.
  }

  const raw = new URLSearchParams(win.location.search);
  if (win.location.search && !hasPaymentReturn(raw)) {
    try {
      win.history.replaceState(win.history.state, '', `${win.location.pathname}${win.location.hash}`);
    } catch {
      // History API blocked — leave the URL as is.
    }
  }
  return merged;
};
